import React, { useEffect, useState, useMemo } from 'react';
import { Row, Col, Card, Tag, Typography, Spin, Empty, Segmented, Avatar, Statistic, Table } from 'antd';
import {
  FileTextOutlined, RiseOutlined, BankOutlined,
  ApartmentOutlined, ArrowUpOutlined, FallOutlined,
  ArrowDownOutlined, UserOutlined, SyncOutlined,
  StarFilled,
  TeamOutlined,
  SubnodeOutlined
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { dashboardActions } from '../../actions/dashboardActions';
import dayjs from 'dayjs';
import TableData from '../../components/table';
import SpinnerLoader from '../../components/spinnerLoader';
import { getblobLink } from '../../services/helper';
import RatingsWidget from '../../components/ratingWidget';
import { reviewActions } from '../../actions/reviewActions';

const { Text, Title } = Typography;

interface dashboardProps {
  role: string
}

const Home: React.FC<dashboardProps> = ({ role }) => {

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState<string>('');
  const [selectedScope, setSelectedScope] = useState<string>('current');

  const commonReportsState = useSelector((state: any) => state.getCommonReportsReducer);
  const schoolRatingsState = useSelector((state: any) => state.getSchoolRatingsReducer);
  const revenueReportState = useSelector((state: any) => state.getRevenueReportReducer);
  const subscriptionStatus = useSelector((state: any) => state.userSubscriptionStatusReducer);

  const hasAccountingAccess = Boolean(subscriptionStatus?.data?.routes?.includes('/accounting'));

  const ratingsData = schoolRatingsState?.data;
  const ratingsSummary = ratingsData && Array.isArray(ratingsData?.data) ? ratingsData?.summary : ratingsData;
  const hasRatings = selectedScope === 'current' && (ratingsSummary?.totalReviews ?? 0) > 0;

  const showRevenueCard = (userRole === 'ADMIN' || userRole === 'SUPER_ADMIN') && hasAccountingAccess;

  const rawCommonData = commonReportsState?.data || {};
  const rawRevenueData = revenueReportState?.data || {};
  const commonScopes = rawCommonData?.scopes || ['current'];

  const commonData = useMemo(() => {
    if (selectedScope === 'current') return rawCommonData?.current || rawCommonData || {};
    if (selectedScope === 'overall') return rawCommonData?.overall || rawCommonData?.current || {};
    if (selectedScope === 'allTenants') return rawCommonData?.allTenants || rawCommonData?.current || {};
    return rawCommonData?.current || rawCommonData || {};
  }, [rawCommonData, selectedScope]);


  const revenueData = useMemo(() => {
    if (selectedScope === 'current') return rawRevenueData?.current || rawRevenueData || {};
    if (selectedScope === 'overall') return rawRevenueData?.overall || rawRevenueData?.current || {};
    if (selectedScope === 'allTenants') return rawRevenueData?.allTenants || rawRevenueData?.current || {};
    return rawRevenueData?.current || rawRevenueData || {};
  }, [rawRevenueData, selectedScope]);

  // Only consider revenueReport's loading state when the logged-in user is ADMIN or SUPER_ADMIN
  const isRevenueRelevant = userRole === 'ADMIN' || userRole === 'SUPER_ADMIN';
  const isLoading = Boolean(commonReportsState?.isLoading) || (isRevenueRelevant && Boolean(revenueReportState?.isLoading));

  const students = commonData?.students || {};
  const recentEnrolments = commonData?.recentEnrolments || [];
  const pendingDocCount = commonData?.pendingDocumentCount || 0;
  const instructorCount = commonData?.instructorCount || 0;

  // Build scope options for the selector
  const scopeOptions = useMemo(() => {
    const options: { label: string; value: string; icon: React.ReactNode }[] = [];
    if (userRole === 'ADMIN') {
      options.push({ label: 'Current Branch', value: 'current', icon: <BankOutlined /> });
      if (commonScopes.includes('overall')) {
        options.push({ label: 'All Branches', value: 'overall', icon: <ApartmentOutlined /> });
      }
    } else if (userRole === 'SUPER_ADMIN') {
      options.push({ label: 'Current Tenant', value: 'current', icon: <BankOutlined /> });
      if (commonScopes.includes('allTenants')) {
        options.push({ label: 'All Tenants', value: 'allTenants', icon: <ApartmentOutlined /> });
      }
    }
    return options;
  }, [userRole, commonScopes]);

  useEffect(() => {
      dispatch(reviewActions.getSchoolRatingsRequest());
    }, [dispatch]);

  useEffect(() => {
    dispatch(dashboardActions.getCommonReports());
    try {
      const authData = JSON.parse(String(localStorage.getItem('AUTHDATA')));
      const resolvedRole = authData?.role || role || '';
      setUserRole(resolvedRole);
      if (resolvedRole === 'ADMIN' || resolvedRole === 'SUPER_ADMIN') {
        dispatch(dashboardActions.getRevenueReport());
      }
    } catch (err) {
      if (role === 'ADMIN' || role === 'SUPER_ADMIN') {
        dispatch(dashboardActions.getRevenueReport());
      }
    }
  }, [dispatch]);

  const columns = [
    {
      title: 'Student',
      dataIndex: 'studentName',
      key: 'studentName',
      render: (text: string, record: any) => (
        <div className="dashboard-table-student">
          <Avatar className="student-avatar" src={`${getblobLink()}/public/asset/male-avatar.png`}>
          </Avatar>
          <div className="student-info-col">
            <div className="student-name-primary">{text || '-'}</div>
            <div className="student-email-secondary">{record.studentEmail || '-'}</div>
          </div>
        </div>
      )
    },
    {
      title: 'Service / Cateogry',
      dataIndex: 'serviceName',
      key: 'serviceName',
      // width: '30%',
      render: (text: string, record: any) => (
        <div className="enrollment-service-info">
          <span className="enrollment-service-name">{record.serviceName}</span>
          <div className="enrollment-category-child">
            <SubnodeOutlined className="enrollment-category-child-icon" />
            <span className="enrollment-category-child-text">{record.categoryName}</span>
          </div>
        </div>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      align: 'center' as const,
      render: (status: string) => {
        const statusConfig: any = {
          'ASSIGNED': { color: 'processing', label: 'Assigned' },
          'IN_PROGRESS': { color: 'warning', label: 'In Progress' },
          'COMPLETED': { color: 'success', label: 'Completed' },
          'CANCELLED': { color: 'error', label: 'Cancelled' },
        };
        const config = statusConfig[status] || { color: 'default', label: status };
        return <Tag style={{ border: "1px solid", borderRadius: '15px' }} color={config.color} className="status-tag-enhanced">{config.label}</Tag>;
      }
    },
    {
      title: 'Documents',
      dataIndex: 'documentStatus',
      key: 'documentStatus',
      width: '18%',
      align: 'center' as const,
      render: (docStatus: string) => {
        const docConfig: any = {
          'Submitted': { color: 'success', label: 'Submitted' },
          'Pending': { color: 'warning', label: 'Pending' },
          'No Documents': { color: 'default', label: 'No Docs' },
        };
        const config = docConfig[docStatus] || { color: 'default', icon: '—', label: docStatus };
        return (
          <Tag style={{ border: "1px solid", borderRadius: '15px' }} color={config.color} className="doc-status-tag-enhanced">
            <span className="doc-icon">{config.icon}</span> {config.label}
          </Tag>
        );
      }
    },
    {
      title: 'Enrolled Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      align: 'center' as const,
      render: (date: string) => (
        <div style={{ textAlign: 'center' }}>
          <div className="date-primary">{dayjs(date).format('DD MMM')}</div>
          <div className="date-year">{dayjs(date).format('YYYY')}</div>
        </div>
      )
    }
  ];

  if (Boolean(commonReportsState?.isLoading) || (isRevenueRelevant && Boolean(revenueReportState?.isLoading))) {
    return <SpinnerLoader />
  }

  return (
    <div className="dashboard-container">

     
      <Row align="middle" justify="space-between" style={{ marginBottom: 16 }} wrap={false}>
        <Col flex="auto"> 
        </Col>
        {scopeOptions.length > 1 && (
          <Col flex="none" style={{ paddingLeft: 16 }}>
            <div style={{
              display: 'flex',
              background: '#f5f5f5',
              borderRadius: 10,
              padding: 2,
              gap: 2,
              border: '1px solid #e8e8e8',
            }}>
              {scopeOptions.map((opt) => {
                const isActive = selectedScope === opt.value;
                return (
                  <button
                    key={opt.value}
                    onClick={() => setSelectedScope(opt.value)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '6px 14px',
                      borderRadius: 7,
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: 13,
                      fontWeight: isActive ? 600 : 400,
                      color: isActive ? '#1d1d1f' : '#8c8c8c',
                      background: isActive ? '#ffffff' : 'transparent',
                      boxShadow: isActive ? '0 1px 4px rgba(0,0,0,0.10)' : 'none',
                      transition: 'all 0.2s ease',
                      whiteSpace: 'nowrap',
                      lineHeight: 1.5,
                    }}
                  >
                    <span style={{
                      fontSize: 13,
                      color: isActive ? '#1890ff' : '#bfbfbf',
                      display: 'flex',
                      alignItems: 'center',
                      transition: 'color 0.2s ease',
                    }}>
                      {opt.icon}
                    </span>
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </Col>
        )}
      </Row>

      <Row gutter={[16, 0]} style={{ marginBottom: 10 }}>
        <Col xs={24} lg={14}>
          <Title level={4} style={{ margin: 0, lineHeight: 1.3 }}>Enrollment Overview</Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Student &amp; instructor statistics
          </Text>
        </Col>
      </Row>
      <Row gutter={[16, 16]} align="stretch">
        <Col xs={24} lg={hasRatings ? 14 : 24}>
          <Row gutter={[16, 16]} className="dashboard-stats-row">

            {/* Card 1 */}
            <Col xs={24} sm={hasRatings ? 12 : (showRevenueCard ? 8 : 12)} md={!hasRatings && !showRevenueCard ? 8 : undefined}>
              <Card bordered={false} className="stat-card" hoverable
                onClick={() => navigate('/users?tab=STUDENT')}
                style={{ cursor: 'pointer', borderRadius: 12, boxShadow: '0 6px 18px rgba(15,15,15,0.06)', height: '100%' }}>
                <Statistic
                  title={<span className="stat-title">Total Students</span>}
                  value={students.totalCount || 0}
                  precision={0}
                  valueStyle={{ color: '#1890ff', fontWeight: 700 }}
                  prefix={<TeamOutlined />}
                  suffix={
                    students.monthlyGrowthPercentage !== undefined && (
                      <span className="stat-suffix">
                        {students.monthlyGrowthPercentage >= 0 ? (
                          <><ArrowUpOutlined /> {students.monthlyGrowthPercentage}%</>
                        ) : (
                          <><ArrowDownOutlined /> {Math.abs(students.monthlyGrowthPercentage)}%</>
                        )}
                      </span>
                    )
                  }
                />
                <div className="stat-meta">
                  This Month: {students.thisMonthCount || 0} | Prev: {students.prevMonthCount || 0}
                </div>
              </Card>
            </Col>

            {/* Card 2 */}
            <Col xs={24} sm={hasRatings ? 12 : (showRevenueCard ? 8 : 12)} md={!hasRatings && !showRevenueCard ? 8 : undefined}>
              <Card bordered={false} className="stat-card" hoverable
                onClick={() => navigate('/users?tab=INSTRUCTOR')}
                style={{ cursor: 'pointer', borderRadius: 12, boxShadow: '0 6px 18px rgba(15,15,15,0.06)', height: '100%' }}>
                <Statistic
                  title={<span className="stat-title">Instructors</span>}
                  value={instructorCount}
                  valueStyle={{ color: '#52c41a', fontWeight: 700 }}
                  prefix={<UserOutlined />}
                />
                <div className="stat-meta">Active instructors</div>
              </Card>
            </Col>

            {/* Card 3 */}
            <Col xs={24} sm={hasRatings ? 12 : (showRevenueCard ? 8 : 12)} md={!hasRatings && !showRevenueCard ? 8 : undefined}>
              <Card bordered={false} className="stat-card" hoverable
                onClick={() => navigate('/enrollments?filter=pendingDocuments')}
                style={{ cursor: 'pointer', borderRadius: 12, boxShadow: '0 6px 18px rgba(15,15,15,0.06)', height: '100%' }}>
                <Statistic
                  title={<span className="stat-title">Pending Docs</span>}
                  value={pendingDocCount}
                  valueStyle={{ color: pendingDocCount > 0 ? '#ff4d4f' : '#52c41a', fontWeight: 700 }}
                  prefix={pendingDocCount > 0 ? <SyncOutlined spin /> : <FileTextOutlined />}
                />
                <div className="stat-meta">Documents to review</div>
              </Card>
            </Col>

            {/* Card 4 (Optional Revenue) */}
            {showRevenueCard && (
              <Col xs={24} sm={hasRatings ? 12 : 8}>
                <Card bordered={false} className="stat-card" hoverable
                  onClick={() => navigate('/accounting?tab=fees')}
                  style={{ cursor: 'pointer', borderRadius: 12, boxShadow: '0 6px 18px rgba(15,15,15,0.06)', height: '100%' }}>
                  <Statistic
                    title={<span className="stat-title">Monthly Revenue</span>}
                    value={revenueData?.profit?.thisMonth || 0}
                    precision={2}
                    prefix="₹"
                    valueStyle={{ color: (revenueData?.profit?.thisMonth || 0) >= 0 ? '#3f8600' : '#cf1322', fontWeight: 700 }}
                    suffix={
                      revenueData?.profit?.thisMonth !== undefined && (
                        <span className="stat-suffix">
                          {revenueData.profit.thisMonth >= 0 ? <RiseOutlined /> : <FallOutlined />}
                        </span>
                      )
                    }
                  />
                  <div className="stat-meta">Net profit this month</div>
                </Card>
              </Col>
            )}

            {/* Card 5 */}
            <Col xs={24} sm={hasRatings ? 12 : (showRevenueCard ? 8 : 12)} md={!hasRatings && !showRevenueCard ? 12 : undefined}>
              <Card bordered={false} className="stat-card"
                style={{ borderRadius: 12, boxShadow: '0 6px 18px rgba(15,15,15,0.06)', height: '100%' }}>
                <Statistic
                  title={<span className="stat-title">Students This Year</span>}
                  value={students.thisYearCount || 0}
                  precision={0}
                  valueStyle={{ color: '#722ed1', fontWeight: 700 }}
                  prefix={<TeamOutlined />}
                  suffix={
                    students.yearlyGrowthPercentage !== undefined && (
                      <span className="stat-suffix">
                        {students.yearlyGrowthPercentage >= 0 ? (
                          <><ArrowUpOutlined /> {students.yearlyGrowthPercentage}%</>
                        ) : (
                          <><ArrowDownOutlined /> {Math.abs(students.yearlyGrowthPercentage)}%</>
                        )}
                      </span>
                    )
                  }
                />
                <div className="stat-meta">
                  Students: {students.prevYearCount || 0} | Growth: {students.yearlyGrowthPercentage?.toFixed(2) || 0}%
                </div>
              </Card>
            </Col>

            {/* Card 6 */}
            <Col xs={24} sm={hasRatings ? (!showRevenueCard ? 24 : 12) : (showRevenueCard ? 8 : 12)} md={!hasRatings && !showRevenueCard ? 12 : undefined}>
              <Card bordered={false} className="stat-card"
                style={{ borderRadius: 12, boxShadow: '0 6px 18px rgba(15,15,15,0.06)', height: '100%' }}>
                <Statistic
                  title={<span className="stat-title">This Month Students</span>}
                  value={students.thisMonthCount || 0}
                  precision={0}
                  valueStyle={{ color: '#13c2c2', fontWeight: 700 }}
                  prefix={<TeamOutlined />}
                />
                <div className="stat-meta">
                  Previous Month: {students.prevMonthCount || 0}
                </div>
              </Card>
            </Col>

          </Row>
        </Col>

        {hasRatings && (
          <Col xs={24} lg={10} style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <RatingsWidget />
            </div>
          </Col>
        )}
      </Row>

      {(userRole === 'ADMIN' || userRole === 'SUPER_ADMIN') && hasAccountingAccess && revenueData.income && (() => {
        const monthlyAvgIncome = Math.round((revenueData.income.thisYear || 0) / 12);
        const incomeVsAvgPct = monthlyAvgIncome > 0
          ? Math.round(((revenueData.income.thisMonth || 0) / monthlyAvgIncome - 1) * 100)
          : 0;
        const yearSharePct = revenueData.income.total > 0
          ? Math.round(((revenueData.income.thisYear || 0) / revenueData.income.total) * 100)
          : 0;
        const reportMax = Math.max(
          Math.abs(revenueData.income.thisMonth || 0),
          Math.abs(revenueData.expenses?.thisMonth || 0),
          Math.abs(revenueData.profit?.thisMonth || 0),
          Math.abs(revenueData.profit?.thisYear || 0),
          1,
        );
        const reportRows = [
          { label: 'Fees Collected (Month)', value: revenueData.income.thisMonth || 0, color: '#1890ff' },
          { label: 'Expenses (Month)', value: revenueData.expenses?.thisMonth || 0, color: '#ff4d4f' },
          { label: 'Net Profit (Month)', value: revenueData.profit?.thisMonth || 0, color: (revenueData.profit?.thisMonth || 0) >= 0 ? '#52c41a' : '#ff4d4f' },
          { label: 'Net Profit (Year)', value: revenueData.profit?.thisYear || 0, color: '#722ed1' },
        ];
        return (
          <Row gutter={[16, 16]} className="mt-4">
            <Col span={24}>
              <Title level={4} style={{ margin: 0 }}>Revenue Breakdown</Title>
              <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Based on student fee transactions</Text>
            </Col>

            <Col xs={24} lg={14}>
              <Row gutter={[0, 16]}>
                <Col span={24}>
                  <Card bordered={false} style={{ borderRadius: 16, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', overflow: 'hidden', border: '1px solid #f0f0f0' }}>
                    {(() => {
                      const thisMonth = revenueData.income.thisMonth || 0;
                      const thisYear = revenueData.income.thisYear || 0;
                      const total = revenueData.income.total || 0;
                      const collectedPct = total > 0 ? Math.round((thisYear / total) * 100) : 0;
                      const remainingPct = 100 - collectedPct;

                      return (
                        <>
                          {/* Header */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                            <div>
                              <Text style={{ fontSize: 11, letterSpacing: 1.2, textTransform: 'uppercase', color: '#8c8c8c', fontWeight: 600 }}>
                                Fees Collected
                              </Text>
                              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 6 }}>
                                <span style={{ fontSize: 36, fontWeight: 800, color: '#1d1d1f', letterSpacing: '-1px', lineHeight: 1 }}>
                                  ₹{thisMonth.toLocaleString()}
                                </span>
                                <Tag style={{
                                  borderRadius: 6, fontSize: 11, fontWeight: 600,
                                  padding: '1px 8px', border: 'none',
                                  background: incomeVsAvgPct >= 0 ? '#f6ffed' : '#fff1f0',
                                  color: incomeVsAvgPct >= 0 ? '#389e0d' : '#cf1322',
                                }}>
                                  {incomeVsAvgPct >= 0 ? '+' : ''}{incomeVsAvgPct}% vs avg
                                </Tag>
                              </div>
                              <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
                                Avg. expected ₹{monthlyAvgIncome.toLocaleString()}/mo
                              </Text>
                            </div>


                          </div>

                          {/* Progress bar — This Year vs All Time */}
                          <div style={{ marginBottom: 20 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                              <Text style={{ fontSize: 11, color: '#595959', fontWeight: 500 }}>
                                This Year
                                <span style={{ marginLeft: 4, fontWeight: 700, color: '#262626' }}>{collectedPct}%</span>
                              </Text>
                              <Text style={{ fontSize: 11, color: '#595959', fontWeight: 500 }}>
                                Prior Years
                                <span style={{ marginLeft: 4, fontWeight: 700, color: '#595959' }}>{remainingPct}%</span>
                              </Text>
                            </div>
                            <div style={{ height: 6, background: '#f0f0f0', borderRadius: 99, overflow: 'hidden', display: 'flex' }}>
                              <div style={{
                                height: '100%', width: `${collectedPct}%`,
                                background: '#1890ff',
                                borderRadius: '99px 0 0 99px',
                                transition: 'width 0.7s cubic-bezier(.4,0,.2,1)'
                              }} />
                              <div style={{
                                height: '100%', width: `${remainingPct}%`,
                                background: '#d9d9d9',
                                borderRadius: '0 99px 99px 0',
                                transition: 'width 0.7s cubic-bezier(.4,0,.2,1)'
                              }} />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 5 }}>
                              <Text type="secondary" style={{ fontSize: 11 }}>₹{thisYear.toLocaleString()}</Text>
                              <Text type="secondary" style={{ fontSize: 11 }}>₹{total.toLocaleString()} total</Text>
                            </div>
                          </div>

                          {/* Bottom stats strip — 3-column */}
                          <div style={{
                            display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
                            background: '#fafafa',
                            borderRadius: 10,
                            border: '1px solid #f0f0f0',
                            overflow: 'hidden'
                          }}>
                            {[
                              { label: 'This Month', value: `₹${thisMonth.toLocaleString()}`, color: '#1890ff' },
                              { label: 'This Year', value: `₹${thisYear.toLocaleString()}`, color: '#262626' },
                              { label: 'All Time', value: `₹${total.toLocaleString()}`, color: '#595959' },
                            ].map((stat, i, arr) => (
                              <div key={stat.label} style={{
                                padding: '12px 16px',
                                borderRight: i < arr.length - 1 ? '1px solid #f0f0f0' : 'none',
                                textAlign: 'center'
                              }}>
                                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                                  {stat.label}
                                </Text>
                                <div style={{ fontWeight: 800, fontSize: 16, color: stat.color, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {stat.value}
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      );
                    })()}
                  </Card>
                </Col>

                {/* Pending Fees */}
                <Col span={24}>
                  <Card bordered={false} style={{ borderRadius: 16, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', overflow: 'hidden', border: '1px solid #f0f0f0' }}>
                    {(() => {
                      const pending = revenueData.pending?.total || 0;
                      const collected = revenueData.income?.thisMonth || 0;
                      const safeTotal = pending + collected || 1;
                      const pendingPct = Math.round((pending / safeTotal) * 100);
                      const collectedPct = 100 - pendingPct;
                      const avgPerStudent = revenueData.pending?.count > 0
                        ? Math.round(pending / revenueData.pending.count)
                        : 0;

                      return (
                        <>
                          {/* Header */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                            <div>
                              <Text style={{ fontSize: 11, letterSpacing: 1.2, textTransform: 'uppercase', color: '#8c8c8c', fontWeight: 600 }}>
                                Pending Fees
                              </Text>
                              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 6 }}>
                                <span style={{ fontSize: 36, fontWeight: 800, color: '#1d1d1f', letterSpacing: '-1px', lineHeight: 1 }}>
                                  ₹{pending.toLocaleString()}
                                </span>
                                <Tag
                                  style={{
                                    borderRadius: 6,
                                    fontSize: 11,
                                    fontWeight: 600,
                                    padding: '1px 8px',
                                    border: 'none',
                                    background: pending > 0 ? '#fff1f0' : '#f6ffed',
                                    color: pending > 0 ? '#cf1322' : '#389e0d',
                                  }}
                                >
                                  {revenueData.pending?.count || 0} unpaid
                                </Tag>
                              </div>
                              <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
                                Uncollected from {revenueData.pending?.count || 0} students
                              </Text>
                            </div>

                            {/* Donut-style radial indicator */}

                          </div>

                          {/* Progress bar */}
                          <div style={{ marginBottom: 20 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                              <Text style={{ fontSize: 11, color: '#595959', fontWeight: 500 }}>
                                Collected
                                <span style={{ marginLeft: 4, fontWeight: 700, color: '#262626' }}>{collectedPct}%</span>
                              </Text>
                              <Text style={{ fontSize: 11, color: '#595959', fontWeight: 500 }}>
                                Pending
                                <span style={{ marginLeft: 4, fontWeight: 700, color: '#cf1322' }}>{pendingPct}%</span>
                              </Text>
                            </div>
                            <div style={{ height: 6, background: '#f0f0f0', borderRadius: 99, overflow: 'hidden', display: 'flex' }}>
                              <div style={{
                                height: '100%', width: `${collectedPct}%`,
                                background: '#52c41a',
                                borderRadius: '99px 0 0 99px',
                                transition: 'width 0.7s cubic-bezier(.4,0,.2,1)'
                              }} />
                              <div style={{
                                height: '100%', width: `${pendingPct}%`,
                                background: '#ff4d4f',
                                borderRadius: '0 99px 99px 0',
                                transition: 'width 0.7s cubic-bezier(.4,0,.2,1)'
                              }} />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 5 }}>
                              <Text type="secondary" style={{ fontSize: 11 }}>₹{collected.toLocaleString()}</Text>
                              <Text type="secondary" style={{ fontSize: 11 }}>₹{pending.toLocaleString()}</Text>
                            </div>
                          </div>

                          {/* Bottom stats strip — clean 3-column */}
                          <div style={{
                            display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
                            background: '#fafafa',
                            borderRadius: 10,
                            border: '1px solid #f0f0f0',
                            overflow: 'hidden'
                          }}>
                            {[
                              { label: 'Students Due', value: String(revenueData.pending?.count || 0), color: '#cf1322' },
                              { label: 'Avg / Student', value: `₹${avgPerStudent.toLocaleString()}`, color: '#d4380d' },
                              { label: 'Due Rate', value: `${pendingPct}%`, color: '#d46b08' },
                            ].map((stat, i, arr) => (
                              <div key={stat.label} style={{
                                padding: '12px 16px',
                                borderRight: i < arr.length - 1 ? '1px solid #f0f0f0' : 'none',
                                textAlign: 'center'
                              }}>
                                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                                  {stat.label}
                                </Text>
                                <div style={{ fontWeight: 800, fontSize: 20, color: stat.color, lineHeight: 1.2 }}>
                                  {stat.value}
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      );
                    })()}
                  </Card>
                </Col>
              </Row>
            </Col>
            <Col xs={24} lg={10}>
              <Card bordered={false} style={{ borderRadius: 16, boxShadow: '0 4px 20px rgba(0,0,0,0.07)', height: '100%' }}>
                <div style={{ marginBottom: 10 }}>
                  <Text style={{ fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase', color: '#8c8c8c', fontWeight: 600 }}>Full Report</Text>
                </div>
                <Title level={2} style={{ margin: '0 0 6px', fontWeight: 800, fontSize: 32, color: '#1d1d1f' }}>
                  ₹{(revenueData.income.total || 0).toLocaleString()}
                </Title>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
                  <span style={{ background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                    <StarFilled style={{ color: '#faad14', fontSize: 10 }} />
                    {yearSharePct}% this year
                  </span>
                  <Text type="secondary" style={{ fontSize: 12 }}>increased total</Text>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: '30px' }}>
                  {reportRows.map(({ label, value, color }) => {
                    const pct = Math.min((Math.abs(value) / reportMax) * 100, 100);
                    return (
                      <div key={label}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                          <Text type="secondary" style={{ fontSize: 12 }}>{label}</Text>
                          <Text strong style={{ fontSize: 13, color }}>₹{value.toLocaleString()}</Text>
                        </div>
                        <div style={{ height: 6, background: '#f0f0f0', borderRadius: 4, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 4, transition: 'width 0.6s ease' }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 1,
                  background: '#f0f0f0',
                  borderRadius: 10,
                  overflow: 'hidden',
                  border: '1px solid #f0f0f0',
                  marginTop: 50
                }}>
                  {[
                    { label: 'Income (Month)', value: `₹${(revenueData.income?.thisMonth || 0).toLocaleString()}`, color: '#1890ff' },
                    { label: 'Income (Year)', value: `₹${(revenueData.income?.thisYear || 0).toLocaleString()}`, color: '#262626' },
                    { label: 'Expenses (Month)', value: `₹${(revenueData.expenses?.thisMonth || 0).toLocaleString()}`, color: '#ff4d4f' },
                    { label: 'Net Profit (Month)', value: `₹${(revenueData.profit?.thisMonth || 0).toLocaleString()}`, color: (revenueData.profit?.thisMonth || 0) >= 0 ? '#389e0d' : '#cf1322' },
                  ].map((stat) => (
                    <div key={stat.label} style={{
                      padding: '12px 16px',
                      background: '#fafafa',
                      textAlign: 'center',
                    }}>
                      <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                        {stat.label}
                      </Text>
                      <div style={{ fontWeight: 800, fontSize: 15, color: stat.color, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {stat.value}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </Col>
          </Row>
        );
      })()}
      <Row gutter={[16, 16]} className="mt-4">
        <Col span={24}>
          <Title style={{ marginBottom: '0', marginTop: '10px' }} level={4} >Recent Enrollments</Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: 4, marginLeft: 1 }}>Based on recent Student enrollment</Text>
        </Col>
        <Col xs={24}>
          {recentEnrolments.length > 0 ? (
            <TableData className='recent-enrollment' columns={columns} style={{ height: '500px', overflow: "hidden !important" }} dataSource={recentEnrolments} pagination={false} rowKey="_id" />
          ) : (
            <Empty description="No recent enrollments" />
          )}
        </Col>
      </Row>
    </div>
  );
};

export default Home;